import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Filter, X } from "lucide-react";

export default function PendingCylinders() {
  const [data, setData] = useState(null);
  const [brands, setBrands] = useState([]);
  const [brandFilter, setBrandFilter] = useState("");  // "" = all

  useEffect(() => {
    api.get("/pending/cylinders").then((r) => setData(r.data));
    api.get("/brands").then((r) => setBrands(r.data));
  }, []);

  // Compute filtered rows: when a brand is selected, total_pending counts only that brand's lines
  const filteredRows = useMemo(() => {
    const rows = data?.rows || [];
    if (!brandFilter) return rows;
    return rows
      .map((r) => {
        const matched = (r.breakdown || []).filter((b) => b.brand === brandFilter && b.pending !== 0);
        const total = matched.reduce((s, b) => s + b.pending, 0);
        return { ...r, breakdown: matched, total_pending: total };
      })
      .filter((r) => r.breakdown.length > 0)
      .sort((a, b) => Math.abs(b.total_pending) - Math.abs(a.total_pending));
  }, [data, brandFilter]);

  const totalsByBrand = useMemo(() => {
    const m = {};
    for (const r of data?.rows || []) {
      for (const b of r.breakdown || []) {
        if (b.pending === 0) continue;
        m[b.brand] = (m[b.brand] || 0) + b.pending;
      }
    }
    return m;
  }, [data]);

  return (
    <div className="space-y-6" data-testid="pending-cylinders-page">
      {/* Brand filter chips */}
      <div className="card-flat p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 mr-3">
            <Filter size={16} className="text-gray-500" />
            <span className="small-label text-gray-500">Filter by Brand</span>
          </div>
          <button
            onClick={() => setBrandFilter("")}
            className={`px-3 py-1.5 text-xs font-bold tracking-wider uppercase rounded-sm border ${brandFilter === "" ? "bg-black text-white border-black" : "bg-white border-gray-300 hover:border-black"}`}
            data-testid="brand-filter-all"
          >
            All
          </button>
          {brands.map((b) => {
            const count = totalsByBrand[b.name] || 0;
            return (
              <button
                key={b.id}
                onClick={() => setBrandFilter(b.name)}
                className={`px-3 py-1.5 text-xs font-bold tracking-wider uppercase rounded-sm border inline-flex items-center gap-1.5 ${brandFilter === b.name ? "bg-black text-white border-black" : "bg-white border-gray-300 hover:border-black"} ${count === 0 ? "opacity-40" : ""}`}
                data-testid={`brand-filter-${b.name}`}
              >
                {b.name}
                {count !== 0 && (
                  <span className={`px-1.5 py-0.5 rounded-sm text-[10px] data-num ${brandFilter === b.name ? "bg-white text-black" : "bg-gray-100 text-gray-700"}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
          {brandFilter && (
            <button
              onClick={() => setBrandFilter("")}
              className="text-xs text-gray-500 hover:text-black inline-flex items-center gap-1 ml-2"
              data-testid="clear-brand-filter"
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </div>

      <div className="card-flat">
        <div className="p-4 border-b border-gray-200 small-label text-gray-500 flex items-center justify-between">
          <span>Cylinder rotation · largest pending first</span>
          {brandFilter && (
            <span className="text-xs text-gray-700 normal-case">
              Showing <strong>{brandFilter}</strong> only · {filteredRows.length} part{filteredRows.length === 1 ? "y" : "ies"}
            </span>
          )}
        </div>
        <div className="overflow-auto">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Party</th>
                <th className="text-left">Type</th>
                <th className="num">{brandFilter ? `${brandFilter} Pending` : "Total Pending"}</th>
                <th className="text-left">Breakdown</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr key={r.party_id} data-testid={`pending-row-${r.party_id}`}>
                  <td className="font-semibold">{r.name}</td>
                  <td className="capitalize">{r.type}</td>
                  <td className={`num font-bold ${r.total_pending > 0 ? "text-[#B45309]" : "text-[#15803D]"}`}>{r.total_pending}</td>
                  <td>
                    <div className="flex flex-wrap gap-1.5">
                      {r.breakdown.filter((b) => b.pending !== 0).map((b, i) => (
                        <span key={i} className={`inline-block text-xs px-2 py-0.5 rounded-sm ${b.pending > 0 ? "stock-mt" : "stock-filled"}`}>
                          {b.brand} {b.size}: {b.pending}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="text-right">
                    <Link to={`/parties/${r.party_id}`} className="text-sm font-semibold hover:underline">Open →</Link>
                  </td>
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr><td colSpan="5" className="py-8 text-center text-gray-400">
                  {brandFilter ? `No pending ${brandFilter} cylinders` : "No pending cylinders 🎉"}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
